import React, { useEffect, useState } from "react";
import { CallToActionButton } from "~/atoms/CallToActionButton";
import { AWARD_ENTRY_CLOSED_AT, AWARD_ENTRY_MAX_TIMEOUT_MS, isAwardEntryClosed } from "~/constant/awardEntry";
import { PATH } from "~/constant/path";
import { HubSpotEmbedForm } from "~/molecules/HubSpotEmbedForm";
import { Layout } from "~/templates/Layout";
import { colors } from "~/utils/colors";

const HUBSPOT_FORM_ID = "f0c8dcfb-2906-4b6c-bd04-ab1854105816";

const PAGE_TITLE = "Referral Recruiting Award 2026 エントリーフォーム";
const PAGE_DESCRIPTION =
  "Referral Recruiting Award 2026のエントリーフォームです。貴社のリファラル採用における優れた取り組みを応募し、採用ブランディング向上や従業員エンゲージメント強化の機会を掴みませんか？企業規模・Refcome利用有無は不問。詳細を記入し、ぜひご応募ください。";
const CLOSED_DESCRIPTION = "Referral Recruiting Award 2026のエントリー受付は終了しました。";

const isPreviewClosed = () => {
  if (typeof window === "undefined") {
    return false;
  }
  return new URLSearchParams(window.location.search).get("previewClosed") === "1";
};

const ReferralRecruitingAwardEntryPage: React.FC = () => {
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    const syncClosedState = () => {
      setClosed(isPreviewClosed() || isAwardEntryClosed());
    };

    syncClosedState();

    if (isPreviewClosed()) {
      return undefined;
    }

    const msUntilClose = AWARD_ENTRY_CLOSED_AT.getTime() - Date.now();
    if (msUntilClose <= 0) {
      return undefined;
    }

    // 終了まで遠いときは定期チェック、近づいたら正確な時刻で切り替える
    if (msUntilClose > AWARD_ENTRY_MAX_TIMEOUT_MS) {
      const intervalId = window.setInterval(syncClosedState, 60 * 60 * 1000);
      return () => {
        window.clearInterval(intervalId);
      };
    }

    const timerId = window.setTimeout(syncClosedState, msUntilClose);
    return () => {
      window.clearTimeout(timerId);
    };
  }, []);

  return (
    <>
      <style jsx>{`
        .wrapper {
          background-color: ${colors.white};
          padding: 10px 0 80px;
        }
        .container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 0 24px;
        }
        .form-container {
          margin: 0 auto;
          max-width: 560px;
          padding-top: 40px;
        }
        .message-container {
          margin: 0 auto;
          max-width: 560px;
          text-align: center;
          padding: 60px 0;
        }
        .message-title {
          font-size: 32px;
          font-weight: bold;
          margin-bottom: 24px;
          color: ${colors.defaultText};
        }
        .message-text {
          font-size: 16px;
          font-weight: 400;
          line-height: 1.8;
          color: ${colors.defaultText};
          margin: 0 0 1.2em;
        }
        .message-text:last-of-type {
          margin-bottom: 32px;
        }
        .message-button {
          display: flex;
          justify-content: center;
          width: 100%;
        }
        .message-button :global(.callToActionButton) {
          width: 100%;
          max-width: 400px;
        }
        @media (max-width: 768px) {
          .container {
            padding: 0 20px;
          }
          .form-container {
            max-width: 100%;
            padding-top: 24px;
          }
          .message-container {
            max-width: 100%;
            padding: 40px 0;
          }
          .message-title {
            font-size: 24px;
          }
          .message-text {
            font-size: 15px;
          }
          .message-text:last-of-type {
            margin-bottom: 28px;
          }
          .message-button :global(.callToActionButton) {
            max-width: 100%;
          }
        }
      `}</style>
      <Layout
        title={PAGE_TITLE}
        ogpDescription={closed ? CLOSED_DESCRIPTION : PAGE_DESCRIPTION}
        noindex
        breadCrumbItems={[PATH.TOP, PATH.EVENTS, PATH.EVENTS_AWARD, PATH.EVENTS_AWARD_ENTRY]}
      >
        <div className="wrapper">
          <div className="container">
            {closed ? (
              <div className="message-container">
                <h1 className="message-title">Referral Recruiting Award 2026</h1>
                <p className="message-text">エントリー受付は終了いたしました。</p>
                <p className="message-text">
                  次回開催の際は、ぜひエントリーをご検討ください。
                  <br />
                  アワードの概要や過去の受賞企業の取り組みは、特設ページからご覧いただけます。
                </p>
                <div className="message-button">
                  <CallToActionButton
                    to={PATH.EVENTS_AWARD.to}
                    backgroundColor={colors.white}
                    textColor={colors.brand}
                    size="100%"
                  >
                    Referral Recruiting Award特設ページへ
                  </CallToActionButton>
                </div>
              </div>
            ) : (
              <div className="form-container" data-hubspot-form-scroll-anchor>
                <HubSpotEmbedForm hubspotFormId={HUBSPOT_FORM_ID} preserveHubSpotContentStyles />
              </div>
            )}
          </div>
        </div>
      </Layout>
    </>
  );
};

export default ReferralRecruitingAwardEntryPage;
